import React, { useState } from "react";
import { useSelector } from "react-redux";
import styles from "./confirmation.module.scss";
import { RootState } from "../../../../../stores/store";
import { PaymentPlan, StartSolarRecommendationRequest } from "../../../../../model/leads/leads";
import { useStartSolarRecommendationMutation } from "../../../../../service/leads-service";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../../../../utils/error-handler";
import { SuccessScreen } from "../../../components/success-screen";
import { Button } from "../../../components";

interface ConfirmationProps {
	onCancel: () => void;
	onConfirm: () => void;
}

const getPaymentPlanLabel = (plan: number | null): string => {
	switch (plan) {
		case PaymentPlan.Installment:
			return "Installment";
		case PaymentPlan.OneTime:
			return "One-time";
		case PaymentPlan.PayAsYouGo:
			return "Pay as you use";
		default:
			return "—";
	}
};

export const Confirmation: React.FC<ConfirmationProps> = ({ onCancel, onConfirm }) => {
	const userInfo = useSelector((state: RootState) => state.user);
	const getSolar = useSelector((state: RootState) => state.getSolar);
	const [startRecommendation, { isLoading }] = useStartSolarRecommendationMutation();
	const [isSuccess, setIsSuccess] = useState(false);

	const handleConfirm = async () => {
		const details = getSolar.paymentPlanDetails;
		const request: StartSolarRecommendationRequest = {
			appliances: getSolar.appliances.map(a => ({
				id: a.applianceId,
				quantity: a.quantity,
				usage: a.dailyUsage,
			})),
			paymentPlan: getSolar.paymentPlan ?? 0,
			paymentPlanDetails: details
				? {
						alternatePowerExpense: details.alternatePowerExpense,
						timelineType: details.timelineType,
						installmentPlanDetails:
							details.solarMonthlyRepayment != null
								? {
										solarMonthlyRepayment: details.solarMonthlyRepayment,
										paymentPeriod: details.paymentPeriod!,
									}
								: null,
						oneTimePlanDetails:
							details.oneTimePaymentAmount != null
								? {
										oneTimePaymentAmount: details.oneTimePaymentAmount,
									}
								: null,
					}
				: {
						alternatePowerExpense: 0,
						timelineType: 0,
						installmentPlanDetails: null,
						oneTimePlanDetails: null,
					},
			installationDetails: {
				authorizedToInstallOnRoof: getSolar.installation?.canAuthorize ?? false,
				numberOfHoursOfElectricityPerDay: Number(getSolar.installation?.electricitySupply) || 0,
				address: getSolar.installation?.installationAddress ?? "",
			},
		};

		try {
			await startRecommendation(request).unwrap();
			setIsSuccess(true);
		} catch (error: any) {
			toast.error(getErrorMessage(error?.data) ?? "An unexpected error occurred");
		}
	};

	if (isSuccess) {
		return (
			<SuccessScreen
				title="Thank you for taking the load assessment."
				subtitle="A Representative will be in touch with you shortly to schedule a Physical Assessment"
				buttonText="Go Home"
				onContinue={onConfirm}
			/>
		);
	}

	return (
		<div className={styles.container}>
			<div className={styles.content}>
				<div className={styles.header}>
					<div className={styles.titleSection}>
						<h2 className={styles.title}>Confirmation</h2>
						<p className={styles.subtitle}>Verify and confirm this transaction</p>
					</div>
				</div>

				<div className={styles.sections}>
					{/* Basic Information */}
					<div className={styles.section}>
						<div className={styles.sectionHeader}>
							<span className={styles.sectionTitle}>Basic information</span>
						</div>
						<div className={styles.sectionBody}>
							<div className={styles.row}>
								<span className={styles.rowLabel}>First name</span>
								<span className={styles.rowValue}>{userInfo.firstName || "—"}</span>
							</div>
							<div className={styles.row}>
								<span className={styles.rowLabel}>Last name</span>
								<span className={styles.rowValue}>{userInfo.lastName || "—"}</span>
							</div>
							<div className={styles.row}>
								<span className={styles.rowLabel}>Email address</span>
								<span className={styles.rowValue}>{userInfo.userName || "—"}</span>
							</div>
							<div className={styles.row}>
								<span className={styles.rowLabel}>Phone number</span>
								<span className={styles.rowValue}>{userInfo.phoneNumber || "—"}</span>
							</div>
						</div>
					</div>

					{/* Appliances */}
					{getSolar.appliances.length > 0 && (
						<div className={styles.section}>
							<div className={styles.sectionHeader}>
								<span className={styles.sectionTitle}>Appliances</span>
							</div>
							<div className={styles.sectionBody}>
								{getSolar.appliances.map((appliance, index) => (
									<div key={`${appliance.applianceId}-${index}`} className={styles.applianceRow}>
										<span className={styles.applianceName}>{appliance.applianceName}</span>
										<span className={styles.applianceDetails}>
											Qty: {appliance.quantity}&nbsp;&nbsp;Daily usage: {appliance.dailyUsage}hrs
										</span>
									</div>
								))}
							</div>
						</div>
					)}

					{/* Payment Plan */}
					<div className={styles.section}>
						<div className={styles.sectionHeader}>
							<span className={styles.sectionTitle}>Payment Plan</span>
						</div>
						<div className={styles.sectionBody}>
							<div className={styles.row}>
								<span className={styles.rowLabel}>Plan</span>
								<span className={styles.rowValue}>{getPaymentPlanLabel(getSolar.paymentPlan)}</span>
							</div>
						</div>
					</div>

					{/* Installation */}
					{getSolar.installation && (
						<div className={styles.section}>
							<div className={styles.sectionHeader}>
								<span className={styles.sectionTitle}>Installation</span>
							</div>
							<div className={styles.sectionBody}>
								<div className={styles.row}>
									<span className={styles.rowLabel}>Authorized for roof installation</span>
									<span className={styles.rowValue}>{getSolar.installation.canAuthorize ? "Yes" : "No"}</span>
								</div>
								<div className={styles.row}>
									<span className={styles.rowLabel}>Electricity supply per day</span>
									<span className={styles.rowValue}>{getSolar.installation.electricitySupply} Hours</span>
								</div>
								{getSolar.installation.installationAddress && (
									<div className={styles.row}>
										<span className={styles.rowLabel}>Installation address</span>
										<span className={styles.rowValue}>{getSolar.installation.installationAddress}</span>
									</div>
								)}
							</div>
						</div>
					)}
				</div>

				<div className={styles.footer}>
					<div className={styles.footerInner}>
						<Button variant="secondary" onClick={onCancel} disabled={isLoading}>
							Cancel
						</Button>
						<Button variant="primary" onClick={handleConfirm} disabled={isLoading}>
							{isLoading ? "Submitting..." : "Confirm"}
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};
